import { notification } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import api from '@/api';

/**
 * Принимает список элементов и ключ активной скважины из модели 'lists'
 * и отдает объект активного элемента.
 * @param {Object[]} listItems - скважины из модели 'lists'.
 * @param {string} activeKey - ключ активной скважины.
 * @returns {Object} информация по активной скважине.
 */
export function getActiveListItem(listItems, activeKey) {
    const activeItem = listItems?.filter(listItem => listItem.key === activeKey)[0];
    let nestedWell;

    if (!activeItem) {
        listItems?.forEach(item => {
            if (item?.children?.filter(listItem => listItem.key === activeKey)[0]) {
                nestedWell = item;
            }
        });
    }

    return activeItem || nestedWell;
}

/**
 * Возвращает метаданные активного элемента модели 'lists'
 * @param {Object} activeListItem - активный элемент модели 'lists'
 * @returns {Object} метаданные активного элемента
 */
export function getMetaDataFromLists(activeListItem) {
    const metaDataFromList = {};

    if (activeListItem.node === 'group') {
        activeListItem.children.forEach(child => {
            metaDataFromList[child.id] = {
                title: child.title,
                key: child.key,
                id: child.id,
            };

            child?.columns &&
                Object.values(child.columns).forEach(column => {
                    metaDataFromList[child.id][column.keyTitle] = column.isVisible;
                });
        });

        return metaDataFromList;
    }

    if (activeListItem.node === 'well' || activeListItem.node === 'shaft') {
        const { id, title, key, columns } = activeListItem;
        metaDataFromList[id] = { title, key, id };

        columns &&
            Object.values(columns).forEach(column => {
                metaDataFromList[id][column.keyTitle] = column.isVisible;
            });

        return metaDataFromList;
    }
}

/**
 * Принимает на вход список пластов и отдает список литологии.
 * Так же подмешиваем в каждый элемент информацию о родительском пласте (id, title) + позицию элемента.
 * @param {Object[]} layersList
 * @returns {Object[]}
 */
export const getLithology = layersList => {
    const result = [];

    for (const layer of layersList) {
        const { boredStratumId, boredStratumTitle } = layer;

        if (layer.children && Array.isArray(layer.children)) {
            const lithology = layer.children.map((lithologyItem, idx) => {
                const stratum = {
                    stratumId: boredStratumId,
                    stratumTitle: boredStratumTitle,
                    stratumCount: idx + 1,
                };

                return { ...lithologyItem, ...stratum };
            });

            result.push(lithology);
        }
    }

    return result.flat();
};

/**
 * Возвращает исследования по списку индивидуальных номеров скважин.
 * @param {string} activeItemKey - активный элемент списка модели 'lists'.
 * @param {Function} requestResearch - функция получения исследования по id скважины.
 * @returns {Promise} объект Promise в результате массив полученных исследований.
 */
export function getWellsResearch(activeItemKey, requestResearch) {
    return requestResearch(activeItemKey);
}

/**
 * Возвращает нормализованные исследования по списку ключей скважин по которым получены исследования.
 * @param {Object[]} activeKeys - список ключей.
 * @param {Function} requestResearch - функция получения нормализованных данных по id скважины.
 * @returns {Promise} объект Promise в результате массив полученных исследований.
 */
export async function getNormalizedResearch(activeKeys, requestResearch) {
    return await requestResearch(activeKeys);
}

/**
 * Обработка результатов запросов, возвращает массив индивидуальных номеров скважин и ключей успешно полученных скважин
 * и массив индивидуальных номеров скважин отклоненных запросов.
 * @param {Object[]} results - массив результатов запросов.
 * @returns {Object}
 * @returns {Object.successWells}
 * @returns {Object.emptyWells}
 * @returns {Object.rejectedIds}
 */
export function processingRequestsResults(results) {
    const successWells = [];
    const emptyWells = [];
    const rejectedIds = [];

    results.forEach(result => {
        const { status, value, reason } = result;

        if (status === 'fulfilled' && value.response.length !== 0) {
            const { id, key } = value;
            successWells.push({ key, id });
        }

        if (value?.response?.length === 0) {
            emptyWells.push(value.id);
        }

        if (status === 'rejected') {
            const { info } = reason;
            rejectedIds.push(info.wellId);
        }
    });

    return { successWells, emptyWells, rejectedIds };
}

/**
 * Из результатов запросов достает исследования, проверяя наличие
 * скважины в успешно завершенных запросах, возвращает их список.
 * @param {Object[]} results - массив результатов запросов
 * @param {Object[]} successWells - массив успешно полученных скважин.
 * @returns {Object[]}
 */
export function getSuccessfulResearches(results, successWells) {
    const successfulResearches = [];
    const ids = successWells.map(item => item.id);

    if (successWells.length === 0) return successfulResearches;

    results.forEach(result => {
        if (ids.includes(result?.value?.id)) {
            successfulResearches.push(result.value);
        }
    });

    return successfulResearches;
}

/**
 * Показывает уведомление об отсутсвии данных по исследованию.
 * @param {Object} options - объект данных для уведомлений.
 * @param {Object[]} [options.emptyWells] - массив индивидуальных номеров скважин без исследований.
 * @param {Object} [options.wellsMetaData] - метаданные по скважинам.
 * @param {Object} [options.messages] - словарь сообщений.
 * @param {Object} [options.name] - название данных.
 * @returns {undefined}
 */
export function sayAboutEmpty({ emptyWells, wellsMetaData, messages, name }) {
    if (emptyWells.length === 0) return;

    if (Object.keys(wellsMetaData).length > 1) {
        return (() => {
            notification.info({
                message: 'Данные отсутствуют',
                duration: 2,
                description: `Для скважин ${emptyWells.map(id => wellsMetaData[id].title)} ${
                    messages[name].emptyMessage
                }`,
            });
        })();
    }

    if (Object.keys(wellsMetaData).length === 1) {
        const [id] = emptyWells;

        return (() => {
            notification.info({
                message: 'Данные отсутствуют',
                duration: 2,
                description: `Для скважины ${wellsMetaData[id].title} ${messages[name].emptyMessage}`,
            });
        })();
    }
}

/**
 * Показывает уведомление об отсутсвии данных по исследованию.
 * @param {Object} options - объект данных для уведомлений.
 * @param {Object[]} [options.rejectedIds] - массив индивидуальных номеров скважин отклоненных запросов.
 * @param {Object} [options.wellsMetaData] - метаданные по скважинам.
 * @param {Object} [options.messages] - словарь сообщений.
 * @param {Object} [options.name] - название данных.
 * @returns {undefined}
 */
export function sayAboutErrors({ rejectedIds, wellsMetaData, messages, name }) {
    if (rejectedIds.length === 0) return;

    // if (Object.keys(wellsMetaData).length > 1) {
    //     return (() => {
    //         notification.error({
    //             message: 'Нет связи с сервером',
    //             duration: 2,
    //             description: `Для скважин ${rejectedIds.map(id => wellsMetaData[id]?.title)} ${
    //                 messages[name].errorMessage
    //             }`,
    //         });
    //     })();
    // }

    // if (Object.keys(wellsMetaData).length === 1) {
    //     const [id] = rejectedIds;

    //     return (() => {
    //         notification.error({
    //             message: 'Нет связи с сервером',
    //             duration: 2,
    //             description: `Для скважины ${wellsMetaData[id]?.title} ${messages[name].errorMessage}`,
    //         });
    //     })();
    // }
}

/**
 * Запускает метод получения списка РИГИС и обрабатывает ошибку запраса,
 * если она есть.
 * @param {Function} getList - метод получения списка.
 * @param {Object[]} ids - список id скважин.
 */

/** TODO: find sayAboutErrorsRigisList function */
export function getRigisList(getList, ids, metaDataFromList) {
    // return getList(ids).catch(() => sayAboutErrorsRigisList(ids, metaDataFromList));
    return getList(ids).catch(e => console.error('getRigisList', e));
}

export function getFgiList(getList, ids, metaDataFromList) {
    // return getList(ids).catch(() => sayAboutErrorsRigisList(ids, metaDataFromList));
    return getList(ids).catch(e => console.error('getFgiList', e));
}

/**
 * Получает отчеты по списку id скважин
 * @param {wellsId} wellsId - массив активных скважин из модели 'lists'
 * @returns {Promise} объект Promise в результате массив полученных отчетов.
 */
export const getGeoObjectsData = async wellsId => {
    try {
        const response = await api.carottage.fetchGeoObjects({ wellsId: wellsId });
        return response;
    } catch (e) {
        const error = new Error(e.message);
        error.info = `Нет данных по скважинам ${wellsId}`;
        throw error;
    }
};

export const getRigis = idsWell => {
    return Promise.allSettled(
        idsWell.map(id => {
            const requestBody = {
                well: id,
                typeReport: 'rigisDataWithoutResidueTree',
            };

            return api.carottage
                .fetchRigisData(requestBody)
                .then(res => ({
                    response: res,
                    wellId: id,
                }))
                .catch(e => {
                    const error = new Error(e.message);
                    error.info = { wellId: id };
                    throw error;
                });
        }),
    );
};

export const getFgi = idsWell => {
    return Promise.allSettled(
        idsWell.map(id => {
            const requestBody = {
                well: id,
            };

            return api.carottage
                .fetchFgiData(requestBody)
                .then(async a => {
                    return { response: await a.json(), status: a.status };
                })
                .then(res => ({
                    response: res.response,
                    status: res.status,
                    wellId: id,
                }))
                .catch(e => {
                    const error = new Error(e.message);
                    error.info = { wellId: id };
                    // console.log(error);
                    throw error;
                });
        }),
    );
};

/**
 * Из результатов запросов достает отчеты, проверяя наличие
 * скважины в успешно завершенных запросах, возвращает их список.
 * @param {Object[]} results - массив результатов запросов
 * @param {Object[]} successWells - массив успешно полученных скважин.
 * @returns {Object[]}
 */
export function getSuccessfulReports(results, successWells) {
    const successfulReports = [];

    if (successWells.length === 0) return successfulReports;

    results.forEach(result => {
        if (successWells.includes(result?.value?.wellId)) {
            successfulReports.push(result.value);
        }
    });

    return successfulReports;
}

/**
 * Обработка результатов запросов, возвращает массив индивидуальных номеров скважин и ключей успешно полученных скважин
 * и массив индивидуальных номеров скважин отклоненных запросов.
 * @param {Object[]} results - массив результатов запросов.
 * @returns {Object}
 * @returns {Object.successWells}
 * @returns {Object.emptyWells}
 * @returns {Object.rejectedIds}
 */
export function processingRequestsReports(results) {
    const successWells = [];
    const emptyWells = [];
    const rejectedIds = [];

    results.forEach(result => {
        const { status, value, reason } = result;

        if (status === 'fulfilled' && value.response.length !== 0) {
            const { wellId } = value;
            successWells.push(wellId);
        }

        if (value?.response?.length === 0) {
            emptyWells.push(value.wellId);
        }

        if (status === 'rejected') {
            const { info } = reason;
            rejectedIds.push(info.wellId);
        }
    });

    return { successWells, emptyWells, rejectedIds };
}

/**
 * На основании данных из списка верский РИГИС и отчетов по исследованиям, нормализует данные
 * о литологии и пластах и возвращает их.
 * @param {Object[]} reports - список отчетов исследований.
 * @param {Object[]} rigisList - список версий РИГИС.
 * @returns {Object}
 * @returns {Object.[id]} - данные скважины по ее id.
 * @returns {Object.rigis}
 * @returns {Object.rigis.layers}
 * @returns {Object.rigis.lithology}
 */
export function extractRigisData(reports, rigisList) {
    const normalizedRigis = {};

    reports?.forEach(report => {
        const { wellId, response } = report;
        const [data] = response;
        let columns = {};
        const versions = [] && data.children;
        const availableVersions = versions.map(version => {
            const { stratumVersionId: reportStratumVersionId } = version;

            const versionIndex = rigisList.findIndex(
                ({ stratumVersionId }) => reportStratumVersionId === stratumVersionId,
            );
            if (versionIndex === -1) {
                return null;
            }

            const visible = rigisList[versionIndex].visible;

            return [reportStratumVersionId, versionIndex, version, visible];
        });

        availableVersions
            .filter(e => e !== null)
            .sort((a, b) => a[0] - b[0])
            .forEach((availableVersion, index) => {
                const [versionId, versionData, visible] = availableVersion;

                const layers = [...versionData.children];

                const column = {
                    lithology: {
                        ...columns.lithology,
                        [versionId]: { data: getLithology(layers), visible: index === 0 ? true : visible },
                    },
                    layers: {
                        ...columns.layers,
                        [versionId]: { data: layers, visible: index === 0 ? true : visible },
                    },
                };

                columns = { ...columns, ...column };

                normalizedRigis[wellId] = { rigis: columns };
            });
    });

    return normalizedRigis;
}

export function extractFgiData(reports, fgiList) {
    const normalizedFgi = {};

    reports?.forEach(report => {
        const { wellId, response } = report;
        const [data] = response;
        let columns = {};
        const versions = [] && data.children;

        const availableVersions = versions.map(version => {
            const { stratumVersionId: reportStratumVersionId } = version;

            const versionIndex = fgiList.findIndex(
                ({ stratumVersionId }) => reportStratumVersionId === stratumVersionId,
            );

            const visible = fgiList[versionIndex].visible;

            return [reportStratumVersionId, versionIndex, version, visible];
        });

        availableVersions
            .sort((a, b) => a[0] - b[0])
            .forEach((availableVersion, index) => {
                const [versionId, versionData, visible] = availableVersion;

                const layers = [...versionData.children];

                const column = {
                    lithology: {
                        ...columns.lithology,
                        [versionId]: { data: getLithology(layers), visible: index === 0 ? true : visible },
                    },
                    layers: {
                        ...columns.layers,
                        [versionId]: { data: layers, visible: index === 0 ? true : visible },
                    },
                };

                columns = { ...columns, ...column };

                normalizedFgi[wellId] = { fgi: columns };
            });
    });

    return normalizedFgi;
}

export function extractGeoObjectsData(
    reports,
    versionsList,
    addCustomGeoObjects,
    addInitCustomGeoObjects,
    customGeoObjects,
) {
    const normalizedGeoObjects = {};
    reports?.forEach(report => {
        const { wellId, children, fieldId, fieldTitle, mineId, mineNumber } = report;
        const layersArr = [];
        const lithology = [];
        const wellCustomGeoObjects = [];
        const availableVersions = versionsList.map((version, i) => {
            version.number = i + 1;
            const childVersion = children.filter(child => child.stratumVersionId === version.stratumVersionId)[0];

            if (childVersion) {
                const description = childVersion.varSource
                    ? childVersion.varSource
                    : childVersion.stratumVersionDescription;
                childVersion.versionTitle = version?.projectDoc ?? version?.description;
                childVersion.versionName = version?.projectDoc ?? version?.description;
                childVersion.number = i + 1;
                if (version.isCustom) {
                    childVersion.date = version.versionCreateDate;
                    childVersion.isCustom = true;
                    childVersion.isPrivate = version.isPrivate;
                    childVersion.readOnly = version.readOnly;
                    childVersion.trueBoredStratumsID = version.trueBoredStratumsID;
                    childVersion.fieldId = report.fieldId;
                    childVersion.fieldTitle = report.fieldTitle;
                    childVersion.mineId = report.mineId;
                    childVersion.mineNumber = report.mineNumber;
                    childVersion.wellTitle = report.wellNumber;
                    childVersion.wellId = report.wellId;
                    childVersion.versionTitle = version?.versionName;
                    childVersion.versionName = version?.versionName;

                    Object.keys(childVersion.trueBoredStratumsID).forEach(key => {
                        if (
                            !customGeoObjects.find(
                                obj =>
                                    obj.trueBoredStratumID === Number(key) &&
                                    obj.versionId === childVersion.stratumVersionId,
                            )
                        ) {
                            const deterministicId = `${childVersion.stratumVersionId}-${key}-${wellId}`;
                            wellCustomGeoObjects.push({
                                ...childVersion.trueBoredStratumsID[key],
                                trueBoredStratumID: Number(key),
                                versionId: childVersion.stratumVersionId,
                                versionTitle: childVersion.versionName,
                                wells_id: [...new Set(childVersion.wells_id)],
                                id: deterministicId,
                            });
                        }
                    });
                }

                const {
                    stratumVersionId: reportStratumVersionId,
                    isCustom,
                    isPrivate,
                    readOnly,
                    versionName,
                    projectDoc,
                    number,
                    date,
                } = childVersion;
                const visible = i === 0 ? true : false;
                const versionTitle = childVersion.versionTitle ?? versionName;
                return [
                    reportStratumVersionId,
                    childVersion,
                    visible,
                    description,
                    versionTitle,
                    isCustom,
                    isPrivate,
                    readOnly,
                    versionName,
                    projectDoc,
                    number,
                    date,
                ];
            } else return null;
        });
        if (wellCustomGeoObjects.length > 0) {
            addCustomGeoObjects(wellCustomGeoObjects);
            addInitCustomGeoObjects(wellCustomGeoObjects);
        }

        availableVersions
            .filter(e => e !== null)
            .forEach((availableVersion, i) => {
                const [
                    versionId,
                    versionData,
                    visible,
                    description,
                    versionTitle,
                    isCustom,
                    isPrivate,
                    readOnly,
                    versionName,
                    projectDoc,
                ] = availableVersion;

                const layers = [...versionData.children];
                const versionDate = versionsList.filter(version => version.stratumVersionId === versionId)[0]?.date
                    ? versionsList.filter(version => version.stratumVersionId === versionId)[0].date
                    : versionsList.filter(version => version.stratumVersionId === versionId)[0]?.versionCreateDate ||
                      '';
                if (!versionData.priority) {
                    layersArr.push({
                        versionId: versionId,
                        data: layers,
                        visible: visible,
                        number: i + 1,
                        description: description ?? '',
                        priority:
                            versionsList.filter(version => version.stratumVersionId === versionId)[0].priority ?? '',
                        date: versionDate,
                        fieldId,
                        fieldTitle,
                        mineId,
                        mineNumber,
                        versionTitle,
                        isCustom,
                        isPrivate,
                        readOnly,
                        versionName,
                        projectDoc,
                    });
                    lithology.push({
                        versionId: versionId,
                        data: getLithology(layers),
                        visible: visible,
                        number: i + 1,
                        description: description ?? '',
                        priority:
                            versionsList.filter(version => version.stratumVersionId === versionId)[0].priority ?? '',
                        date: versionDate,
                        fieldId,
                        fieldTitle,
                        mineId,
                        mineNumber,
                        versionTitle,
                        isCustom,
                        isPrivate,
                        readOnly,
                        versionName,
                        projectDoc,
                    });
                } else
                    layersArr.push({
                        versionId: versionId,
                        data: layers,
                        visible: visible,
                        number: i + 1,
                        description: description ?? '',
                        priority:
                            versionsList.filter(version => version.stratumVersionId === versionId)[0].priority ?? '',
                        date: versionDate,
                        fieldId,
                        fieldTitle,
                        mineId,
                        mineNumber,
                        versionTitle,
                        isCustom,
                        isPrivate,
                        readOnly,
                        versionName,
                        projectDoc,
                    });
            });

        normalizedGeoObjects[wellId] = { geoObjects: { lithology: lithology, layers: layersArr } };
    });

    return normalizedGeoObjects;
}

export const reorderSecondArrayWithDifferentKeys = (array1, array2, key1, key2) => {
    if (array1 && array2) {
        const array2Map = array2.reduce((map, obj) => {
            map[obj[key2]] = obj;
            return map;
        }, {});

        const reorderedElements = array1.map(item => array2Map[item[key1]]).filter(Boolean);

        const remainingElements = array2.filter(item => !array1.some(el => el[key1] === item[key2]));

        return [...reorderedElements, ...remainingElements];
    }
};

/**
 * На основании данных из модели списков, формирует и возвращает объект со скважиными группы
 * и состоянием видимости колонок для каждой скважины.
 * @param {Object[]} metaData - список скважин с колонками.
 * @returns {Object}
 * @returns {Object.[id]} - данные скважины по ее id.
 */
export function formGroupByMeta(metaData) {
    const result = {};

    metaData.forEach(itemMeta => {
        const [id, data] = itemMeta;

        Object.entries(data).forEach(objectProperty => {
            const [key, value] = objectProperty;
            if (typeof value === 'boolean') {
                result[Number(id)] = {
                    ...result?.[Number(id)],
                    [key]: {
                        visible: value,
                    },
                };
                return;
            }
            result[Number(id)] = {
                ...result?.[Number(id)],
                [key]: value,
            };
        });
    });

    return result;
}

/**
 * На основании данных из модели списков, формирует и возвращает объект скважины
 * и состоянием видимости колонок нее.
 * @param {Object[]} metaData - список ключей и значений скважин.
 * @returns {Object} - метадата о скважине с ее колонками
 */
export function formTabletByMeta(metaData) {
    const result = {};

    metaData.forEach(objectProperty => {
        const [key, value] = objectProperty;
        if (typeof value === 'boolean') {
            result[key] = {
                visible: value,
            };
            return;
        }
        result[key] = value;
    });

    return result;
}

export const getMergedFlatGeoObjects = (versions, geoObjects) => {
    versions.forEach(version => {
        geoObjects.forEach(geoObject => {
            if (version.wells_id.includes(geoObject.wellId)) {
                const customGeoVersionChildren = [];
                let priority = 0;
                geoObject.children.forEach(geoVersion => {
                    priority = geoVersion.priority ?? 0;

                    geoVersion.children.forEach(versionChild => {
                        if (
                            Object.keys(version.trueBoredStratumsID).includes(
                                versionChild.trueBoredStratumID.toString(),
                            )
                        ) {
                            customGeoVersionChildren.push(versionChild);
                        }
                    });
                });

                if (customGeoVersionChildren.length > 0) {
                    const uniqueCustomGeoVersionChildren = customGeoVersionChildren.reduce((acc, current) => {
                        const exists = acc.find(item => item.trueBoredStratumID === current.trueBoredStratumID);
                        if (!exists) {
                            acc.push(current);
                        }
                        return acc;
                    }, []);
                    const customGeoVersion = {
                        stratumVersionId: version.stratumVersionId,
                        stratumVersionDate: version.versionCreateDate,
                        stratumVersionDescription: version.description,
                        children: uniqueCustomGeoVersionChildren,
                        priority: priority,
                    };
                    geoObject.children.push(customGeoVersion);
                }
            }
        });
    });

    return geoObjects;
};

export const reorderArrayByKey = (arr, refArr, key) =>
    arr.sort((a, b) => {
        const idxA = refArr.findIndex(ref => ref[key] === a[key]);
        const idxB = refArr.findIndex(ref => ref[key] === b[key]);

        if (idxA === -1 && idxB === -1) return 0;
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;

        return idxA - idxB;
    });
export const reorderArrayByStratumVersionId = (arrayToReorder, orderReferenceArray) => {
    const orderMap = new Map(orderReferenceArray.map((item, index) => [item.stratumVersionId, index]));

    const inReference = [];
    const notInReference = [];

    arrayToReorder.forEach(item => {
        if (orderMap.has(item.stratumVersionId)) {
            inReference.push(item);
        } else {
            notInReference.push(item);
        }
    });

    inReference.sort((a, b) => {
        return orderMap.get(a.stratumVersionId) - orderMap.get(b.stratumVersionId);
    });

    return [...inReference, ...notInReference];
};

export const processGroupVersionCleanup = (version, list, extractedData, lists, dispatch) => {
    const groupItems = lists.data?.filter(item => item.node === 'group') || [];

    groupItems.forEach(groupItem => {
        const objKey = groupItem.key;
        const groupList = list[objKey];

        if (!groupList) return;

        const wellsToUpdate = Object.keys(groupList).filter(wellKey => {
            const wellId = Number(wellKey);
            return version.wells_id && !version.wells_id.includes(wellId);
        });

        const updatedWells = {};

        wellsToUpdate.forEach(wellKey => {
            const wellId = Number(wellKey);
            const listItem = groupList[wellKey];

            if (listItem?.geoObjects?.versions) {
                updatedWells[wellKey] = {
                    ...listItem,
                    geoObjects: {
                        ...listItem.geoObjects,
                        versions: listItem.geoObjects.versions.filter(
                            ver => ver.versionId !== version.stratumVersionId,
                        ),
                    },
                };
            }

            if (extractedData[wellId]?.geoObjects?.layers) {
                extractedData[wellId].geoObjects.layers = extractedData[wellId].geoObjects.layers.filter(
                    ver => ver.versionId !== version.stratumVersionId,
                );
            }
        });

        if (Object.keys(updatedWells).length > 0) {
            Object.keys(updatedWells).forEach(wellKey => {
                dispatch.tablets.updateTablets({
                    activeGroupName: objKey,
                    geoObjects: extractedData,
                    geoObjectsId: wellKey,
                });
            });
        }
    });
};

export const getAllListsWellIds = lists => {
    const allWellsIds = [];
    lists.data.forEach(item => {
        if (item.node === 'well') allWellsIds.push(item.id);
        if (item.children) {
            item.children.forEach(child => {
                allWellsIds.push(child.id);
            });
        }
    });
    return allWellsIds;
};
